import { DateTime } from "luxon";
import { OrderStatusEnum } from "@src/features/order/entities";
import { DEFAULT_ORDER_FILTERS, ORDER_TYPE_OPTIONS } from "./constants";
import { DatePreset, OrderFilters } from "./types";

const STATUS_PARAM = "status";
const HUB_PARAM = "hubId";
const DATE_PRESET_PARAM = "datePreset";
const DATE_FROM_PARAM = "dateFrom";
const DATE_TO_PARAM = "dateTo";

const SELECTABLE_STATUSES = ORDER_TYPE_OPTIONS.map((option) => option.value);
const DATE_PRESETS = Object.values(DatePreset);
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const POSITIVE_INTEGER = /^[1-9]\d*$/;

const toOrderStatus = (raw: string): OrderStatusEnum | null => {
  const status = raw.toLowerCase() as OrderStatusEnum;
  return SELECTABLE_STATUSES.includes(status) ? status : null;
};

const isNonEmpty = <T>(value: T | null): value is T => value !== null;

const parseDate = (searchParams: URLSearchParams, key: string): string | null => {
  const raw = searchParams.get(key);
  if (!raw || !ISO_DAY.test(raw)) return null;
  return DateTime.fromISO(raw).isValid ? raw : null;
};

export const parseOrderFilters = (searchParams: URLSearchParams): OrderFilters => {
  const orderTypes = searchParams.getAll(STATUS_PARAM).map(toOrderStatus).filter(isNonEmpty);

  const hubIds = searchParams
    .getAll(HUB_PARAM)
    .filter((raw) => POSITIVE_INTEGER.test(raw))
    .map(Number);

  const rawPreset = searchParams.get(DATE_PRESET_PARAM) as DatePreset | null;
  const datePreset = rawPreset && DATE_PRESETS.includes(rawPreset) ? rawPreset : DatePreset.ALL_TIME;

  const customStart = parseDate(searchParams, DATE_FROM_PARAM);
  const customEnd = parseDate(searchParams, DATE_TO_PARAM);

  if (datePreset === DatePreset.CUSTOM && (!customStart || !customEnd)) {
    return { ...DEFAULT_ORDER_FILTERS, orderTypes, hubIds };
  }

  return {
    orderTypes,
    hubIds,
    datePreset,
    customStart: datePreset === DatePreset.CUSTOM ? customStart : null,
    customEnd: datePreset === DatePreset.CUSTOM ? customEnd : null,
  };
};

export const writeOrderFilters = (
  searchParams: URLSearchParams,
  filters: OrderFilters,
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  [STATUS_PARAM, HUB_PARAM, DATE_PRESET_PARAM, DATE_FROM_PARAM, DATE_TO_PARAM].forEach((key) =>
    next.delete(key),
  );

  filters.orderTypes.forEach((status) => next.append(STATUS_PARAM, status.toUpperCase()));
  filters.hubIds.forEach((hubId) => next.append(HUB_PARAM, String(hubId)));

  if (filters.datePreset !== DatePreset.ALL_TIME) {
    next.set(DATE_PRESET_PARAM, filters.datePreset);
  }
  if (filters.datePreset === DatePreset.CUSTOM && filters.customStart && filters.customEnd) {
    next.set(DATE_FROM_PARAM, filters.customStart);
    next.set(DATE_TO_PARAM, filters.customEnd);
  }

  return next;
};

export const areOrderFiltersEqual = (a: OrderFilters, b: OrderFilters): boolean =>
  a.datePreset === b.datePreset &&
  a.customStart === b.customStart &&
  a.customEnd === b.customEnd &&
  a.orderTypes.length === b.orderTypes.length &&
  a.orderTypes.every((status) => b.orderTypes.includes(status)) &&
  a.hubIds.length === b.hubIds.length &&
  a.hubIds.every((hubId) => b.hubIds.includes(hubId));
